import { useParams, Link } from "react-router-dom";
import { articles, featuredVideos, hotVideos, categoryVideos, categories } from "@/mocks/data";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useMemo } from "react";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);

  const article = useMemo(() => {
    const art = articles.find((a) => a.id === articleId);
    if (art) return { type: "article" as const, data: art };

    const allVideos = [...featuredVideos, ...hotVideos, ...Object.values(categoryVideos).flat()];
    const uniqueVideos = allVideos.filter((v, i, arr) => arr.findIndex((x) => x.id === v.id) === i);
    const vid = uniqueVideos.find((v) => v.id === articleId);
    if (vid) return { type: "video" as const, data: vid };

    return null;
  }, [articleId]);

  if (!article) {
    return (
      <main className="min-h-screen pt-24 px-4 md:px-6">
        <div className="max-w-[1200px] mx-auto text-center py-20">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-900 mb-2">内容未找到</h2>
          <Link to="/" className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">返回首页</Link>
        </div>
      </main>
    );
  }

  const catName = article.type === "video"
    ? categories.find((c) => c.slug === article.data.category)?.name || article.data.category
    : article.data.category;

  if (article.type === "video") {
    const v = article.data;
    return (
      <main className="min-h-screen pt-24 pb-14 px-4 md:px-6">
        <article className="max-w-[900px] mx-auto">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: catName, href: `/category/${v.category}` },
                { label: v.title },
              ]}
            />
          </div>

          <div className="relative rounded-lg overflow-hidden mb-8 aspect-video">
            <img
              src={v.image}
              alt={v.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-center justify-center">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 cursor-pointer hover:bg-primary-600/90 transition-colors">
                <i className="ri-play-fill text-4xl ml-1"></i>
              </div>
            </div>
            {v.quality && (
              <span className="absolute top-4 right-4 px-3 py-1 rounded-md bg-background-950/70 text-background-50 text-xs font-medium backdrop-blur-sm">
                {v.quality}
              </span>
            )}
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
            {v.title}
          </h1>

          <div className="flex items-center gap-4 mb-6 flex-wrap">
            {v.rating && (
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-amber-400"></i>
                <span className="text-sm font-semibold text-foreground-900">{v.rating}</span>
              </div>
            )}
            {v.year && <span className="text-sm text-foreground-500">{v.year}</span>}
            {v.source && <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700">片源：{v.source}</span>}
            {v.tags && v.tags.map((tag) => (
              <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600">{tag}</span>
            ))}
          </div>

          <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
            <p>{v.description}</p>
            <p>久久视频为您提供{v.title}的蓝光画质超清播放服务。本片来源于{v.source || "久久视频合作平台"}，支持多分辨率切换，流畅观影体验。无论您使用手机、平板还是电脑，都可以随时随地免费在线观看。</p>
          </div>

          <div className="mt-8 p-5 rounded-lg bg-background-100 border border-background-200">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3">观影提示</h3>
            <ul className="space-y-2 text-sm text-foreground-600">
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-primary-500 mt-0.5"></i>
                本站提供蓝光画质播放，建议在WiFi环境下观看以获得最佳体验
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-primary-500 mt-0.5"></i>
                如遇加载缓慢，可尝试切换至1080P画质
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-primary-500 mt-0.5"></i>
                支持手机、平板、电脑多端同步观看
              </li>
            </ul>
          </div>
        </article>
      </main>
    );
  }

  const a = article.data;
  return (
    <main className="min-h-screen pt-24 pb-14 px-4 md:px-6">
      <article className="max-w-[800px] mx-auto">
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: "影视资讯" },
              { label: a.title },
            ]}
          />
        </div>

        <div className="rounded-lg overflow-hidden mb-8 aspect-[2/1]">
          <img
            src={a.image}
            alt={a.title}
            className="w-full h-full object-cover"
          />
        </div>

        <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
          {a.title}
        </h1>

        <div className="flex items-center gap-4 mb-8 flex-wrap">
          <span className="text-sm text-foreground-500">{a.date}</span>
          <span className="text-sm text-foreground-500">{a.author}</span>
          {a.tags && a.tags.map((tag) => (
            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700">{tag}</span>
          ))}
        </div>

        <div
          className="prose prose-sm max-w-none text-foreground-700 leading-relaxed [&>h3]:text-lg [&>h3]:font-bold [&>h3]:text-foreground-900 [&>h3]:mt-8 [&>h3]:mb-3 [&>p]:mb-4 [&>p]:text-[15px] [&>p]:leading-7"
          dangerouslySetInnerHTML={{ __html: a.content }}
        />

        <div className="mt-10 pt-8 border-t border-background-200">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
            <div className="flex items-center gap-3">
              <span className="text-xs text-foreground-500">分享：</span>
              <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </button>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}